import { useQuery } from '@tanstack/react-query';
import { ObjectMeta } from 'kubernetes-types/meta/v1';
import {
  ConfigMapList,
  SecretList,
  ServiceList,
} from 'kubernetes-types/core/v1';
import { DeploymentList } from 'kubernetes-types/apps/v1';

import axios from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { getPods } from '@/react/kubernetes/applications/queries/getPods';
import { GenericResource } from '@/react/kubernetes/helm/types';

// Refreshed so pod/deployment churn shows while debugging a slow or failing
// install.
const REFRESH_INTERVAL = 10000;

/**
 * useNamespaceResources lists the core workload and config resources in a
 * namespace (pods, deployments, config maps, secrets, services) as a flat
 * GenericResource[], so the add-on Resources tab can show what's in the
 * namespace when the Helm release itself doesn't exist yet.
 */
export function useNamespaceResources(
  environmentId: EnvironmentId,
  namespace: string,
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery(
    ['addon-namespace-resources', environmentId, namespace],
    () => getNamespaceResources(environmentId, namespace),
    {
      enabled: enabled && !!environmentId && !!namespace,
      refetchInterval: REFRESH_INTERVAL,
    }
  );
}

async function getNamespaceResources(
  environmentId: EnvironmentId,
  namespace: string
): Promise<GenericResource[]> {
  const base = `/endpoints/${environmentId}/kubernetes`;
  const ns = `namespaces/${namespace}`;

  // Best-effort per kind. This is a limited list for simplicity and showing what is important, even when the release doesn't exist
  const [
    podsResult,
    deploymentsResult,
    servicesResult,
    configMapsResult,
    secretsResult,
  ] = await Promise.allSettled([
    getPods(environmentId, namespace),
    listItems<DeploymentList>(`${base}/apis/apps/v1/${ns}/deployments`),
    listItems<ServiceList>(`${base}/api/v1/${ns}/services`),
    listItems<ConfigMapList>(`${base}/api/v1/${ns}/configmaps`),
    listItems<SecretList>(`${base}/api/v1/${ns}/secrets`),
  ]);

  const pods = podsResult.status === 'fulfilled' ? podsResult.value : [];
  const deployments =
    deploymentsResult.status === 'fulfilled' ? deploymentsResult.value : [];
  const services =
    servicesResult.status === 'fulfilled' ? servicesResult.value : [];
  const configMaps =
    configMapsResult.status === 'fulfilled' ? configMapsResult.value : [];
  const secrets =
    secretsResult.status === 'fulfilled' ? secretsResult.value : [];

  return [
    // Pods carry a phase, the key signal when an install is stuck; other kinds
    // have no meaningful status here, so they render status-less.
    ...pods.map((pod) => toResource('Pod', pod.metadata, pod.status?.phase)),
    ...deployments.map((d) => toResource('Deployment', d.metadata)),
    ...services.map((s) => toResource('Service', s.metadata)),
    ...configMaps.map((c) => toResource('ConfigMap', c.metadata)),
    ...secrets.map((s) => toResource('Secret', s.metadata)),
  ];
}

async function listItems<T extends { items: unknown[] }>(
  url: string
): Promise<T['items']> {
  const { data } = await axios.get<T>(url);
  return data.items;
}

function toResource(
  kind: string,
  meta: ObjectMeta | undefined,
  phase?: string
): GenericResource {
  return {
    kind,
    metadata: {
      name: meta?.name ?? '',
      namespace: meta?.namespace,
      uid: meta?.uid,
    },
    status: phase ? { healthSummary: { status: '', reason: phase } } : {},
  };
}
